import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircleHeart, Download } from "lucide-react";
import { toast } from "sonner";
import { getThread } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";

/**
 * Inline Premium-gated keepsake download. Hits GET
 * /api/cards/{id}/thread.pdf which 402s if the user isn't premium.
 * We surface the gate as a soft amber toast → /?premium=1 route.
 */
function ThreadKeepsakeCTA({ moodId }) {
    const { isPremium, user } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const handleDownload = async () => {
        if (!user) {
            toast("Sign in to keep this correspondence as a PDF.", {
                action: { label: "Sign in", onClick: () => navigate("/") },
            });
            return;
        }
        if (!isPremium) {
            toast("Thread keepsake PDF is a Premium feature — sit with this one or upgrade.", {
                action: { label: "See plans", onClick: () => navigate("/?premium=1") },
                duration: 6000,
            });
            return;
        }
        setBusy(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/cards/${moodId}/thread.pdf`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) {
                if (res.status === 402) throw new Error("Premium required");
                throw new Error(`Download failed (${res.status})`);
            }
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `moodcard-correspondence-${moodId.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            track("thread_pdf_downloaded", { mood_id: moodId });
            toast.success("kept · check your downloads");
        } catch (e) {
            toast.error(e.message || "Couldn't download right now");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-8 flex justify-center">
            <button
                onClick={handleDownload}
                disabled={busy}
                data-testid="thread-keepsake-pdf"
                className="btn-press inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-amber-300/30 bg-amber-400/[0.05] text-amber-200/85 hover:bg-amber-400/[0.10] hover:text-amber-100 font-sans text-[11px] uppercase tracking-[0.32em] transition-colors disabled:opacity-50"
            >
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span>{busy ? "keeping…" : "keep as pdf"}</span>
                {!isPremium && (
                    <span className="ml-1 text-[9px] tracking-[0.28em] text-amber-200/65">· premium</span>
                )}
            </button>
        </div>
    );
}


/**
 * Thread — quiet correspondence timeline.
 *
 * Loads the ancestor chain ending at :id and renders each turn as a
 * stacked, museum-clean card-stub. No avatars, no chat bubbles, no
 * timestamps in the chrome — just sender name, feeling, style, and a
 * subtle date. Tap any stub to open that card in full.
 *
 * Design notes:
 *   • Vertical timeline with a thin connector line on the left.
 *   • Each turn alternates subtle accent (amber for "you", neutral for
 *     "them") — based on whether sender_name matches the FIRST visible
 *     sender (heuristic; we don't know the viewer identity here).
 *   • If thread length is 1, render a "no correspondence yet" empty state
 *     pointing back to the card. We DO NOT 404 short threads — the link
 *     is just hidden on the surfacing surfaces (Dashboard/CardDetail) at
 *     <3 cards. Anyone with a direct URL can still browse.
 */
export default function Thread() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await getThread(id);
                if (!cancelled) {
                    setData(res);
                    track("thread_viewed", { id, length: res.count || 0 });
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    if (error === "not_found") {
        return (
            <div className="min-h-screen w-full aurora-wash flex items-center justify-center px-6" data-testid="thread-not-found">
                <div className="text-center">
                    <p className="font-serif italic text-app-2 text-xl mb-4">this thread drifted away.</p>
                    <button
                        onClick={() => navigate("/")}
                        className="btn-press font-sans text-xs uppercase tracking-[0.3em] text-app-3 hover:text-app py-2 px-4"
                    >
                        back home
                    </button>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen w-full aurora-wash flex items-center justify-center" data-testid="thread-loading">
                <span className="h-1.5 w-1.5 rounded-full pulse-soft" style={{ backgroundColor: "#d4af6e" }} />
            </div>
        );
    }

    const thread = data.thread || [];
    // First sender's name = our "anchor" — visualized in amber. Everyone else
    // reads as neutral. (Two-party convo is the dominant case; 3+-party
    // threads exist but feel rare enough that we can ship the simpler look.)
    const anchorSender = thread[0]?.sender_name || null;

    return (
        <div className="relative min-h-screen w-full aurora-wash overflow-hidden" data-testid="thread-screen">
            <div className="absolute inset-0 grain pointer-events-none" />

            {/* Top bar — minimal back affordance + label */}
            <header className="sticky top-0 z-10 max-w-md mx-auto px-6 pt-5 pb-3 backdrop-blur-md"
                style={{ backgroundColor: "color-mix(in srgb, var(--surface-1) 78%, transparent)" }}
            >
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        data-testid="thread-back-button"
                        aria-label="Back"
                        className="btn-press flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    <div className="flex items-center gap-2">
                        <MessageCircleHeart className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                        <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-2">
                            correspondence
                        </span>
                    </div>
                    <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-app-3" data-testid="thread-count">
                        {thread.length}
                    </span>
                </div>
            </header>

            <main className="relative z-10 max-w-md mx-auto px-6 pt-4 pb-24">
                {/* Vertical timeline rail */}
                <div className="relative pl-6">
                    <span
                        aria-hidden="true"
                        className="absolute left-[6px] top-3 bottom-3 w-px"
                        style={{ background: "linear-gradient(to bottom, rgba(212,175,110,0.0), rgba(212,175,110,0.45), rgba(212,175,110,0.0))" }}
                    />

                    <ul className="space-y-7" data-testid="thread-list">
                        {thread.map((turn, i) => {
                            const isAnchor = !!turn.sender_name && turn.sender_name === anchorSender;
                            const sender = turn.sender_name || "anonymous";
                            const date = turn.created_at
                                ? new Date(turn.created_at).toLocaleDateString(undefined, {
                                      month: "short", day: "numeric",
                                      hour: "numeric", minute: "2-digit",
                                  })
                                : "";
                            return (
                                <motion.li
                                    key={turn.id}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
                                    data-testid={`thread-turn-${i}`}
                                    className="relative"
                                >
                                    {/* timeline dot */}
                                    <span
                                        aria-hidden="true"
                                        className="absolute -left-[26px] top-2 h-2 w-2 rounded-full"
                                        style={{
                                            background: isAnchor ? "#d4af6e" : "var(--app-text-3)",
                                            boxShadow: isAnchor ? "0 0 10px rgba(212,175,110,0.55)" : "none",
                                        }}
                                    />

                                    <button
                                        onClick={() => turn.slug && navigate(`/open/${turn.slug}`)}
                                        disabled={!turn.slug}
                                        data-testid={`thread-turn-button-${i}`}
                                        className={`btn-press w-full text-left rounded-2xl border bg-surface-1 p-4 transition-colors hover:bg-surface-2 disabled:cursor-default ${
                                            isAnchor ? "border-amber-300/30" : "border-app/20"
                                        }`}
                                    >
                                        <div className="flex items-baseline justify-between gap-3 mb-2">
                                            <span
                                                className={`font-sans text-[10px] uppercase tracking-[0.32em] ${
                                                    isAnchor ? "text-amber-200/80" : "text-app-3"
                                                }`}
                                            >
                                                {sender}
                                            </span>
                                            {date && (
                                                <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-app-3 shrink-0">
                                                    {date}
                                                </span>
                                            )}
                                        </div>
                                        <p className="font-serif italic text-app text-[15px] leading-snug">
                                            "{turn.feeling.length > 140 ? turn.feeling.slice(0, 140) + "…" : turn.feeling}"
                                        </p>
                                        {turn.style && (
                                            <span className="mt-2 inline-block font-sans text-[9px] uppercase tracking-[0.3em] text-app-3">
                                                {turn.style}
                                            </span>
                                        )}
                                    </button>
                                </motion.li>
                            );
                        })}
                    </ul>
                </div>

                {/* Quiet footer note */}
                <p className="mt-10 text-center font-serif italic text-[11px] text-app-3 opacity-75">
                    kept here · the cards you actually exchanged.
                </p>

                {/* Keepsake PDF download — Premium gated.
                    Premium users get a printable A5 multi-page PDF of the
                    full correspondence (one page per turn). Free users see
                    the button but get a paywall toast on click. */}
                {thread.length >= 1 && <ThreadKeepsakeCTA moodId={id} />}
            </main>
        </div>
    );
}
